import * as OrderService from '@/services/api/orderService';
import { generateIDNumber, replacePlaceholders } from '../../utils/file';
import { Domain } from '@/types/api/Business/Domain';
import { apiExecutePost } from './api';
import { removeWordFromString } from '@/utils/formBuilder';
import { getGeneratedPDFBlob, getOrderConfirmationPDFTemplate, getOrderConfirmationPDFTemplateWjtOrtho } from '@/utils/pdfTemplates';
import * as UserService from '@/services/api/userService';
import { useUserStore } from '@/stores/userStore';
import i18n from '@/i18n';
import { useThemeStore } from '@/stores/themeStore';

const emailServiceUrl = import.meta.env.VITE_MAIL_SERVICE_URL;
const aidddoLogoUrl = 'https://aidddoblob.blob.core.windows.net/public/Logo_Aidddo.jpg';
const wjtorthoLogoUrl = 'https://aidddoblob.blob.core.windows.net/public/Logo_Wjtortho.jpg';
const luttermannLogoUrl = 'https://aidddoblob.blob.core.windows.net/public/Logo_Lutterman.jpg';
const cekoonLogoUrl = 'https://aidddoblob.blob.core.windows.net/public/Logo_Cekoon.jpg';
const spoererLogoUrl = 'https://aidddoblob.blob.core.windows.net/public/Logo_Spoerer.jpg';

//central mapping + helpers
const LOGO_BY_SUFFIX: Record<string, string> = {
  wjtortho: wjtorthoLogoUrl,
  luttermann: luttermannLogoUrl,
  lutterman: luttermannLogoUrl,
  aidddo: aidddoLogoUrl,
  cekoon: cekoonLogoUrl,
  spoerer: spoererLogoUrl,
};

const suffixToken = (s?: string) => (s || '').split('.')[0].trim().toLowerCase();

function resolveVisuals(domain: Domain) {
  const themeStore = useThemeStore();
  const bgColor = themeStore.primaryColor;
  const textColor = themeStore.primaryText;
  const key = suffixToken(domain.urlSuffix);
  const logoUrl = domain.logo || LOGO_BY_SUFFIX[key] || cekoonLogoUrl;
  return { bgColor, textColor, logoUrl };
}

function assertNotSpaIndex(html: string, domain: Domain) {
  // no-op now; we handle SPA detection with a fallback renderer below
  if (/<noscript>.*doesn'?t work properly without JavaScript/i.test(html)) {
    console.warn(`Email template for "${domain.urlSuffix}" looks like a SPA; using fallback email HTML.`);
  }
}

//detect SPA index & provide safe fallback email HTML
function isLikelySpaIndex(html: string) {
  return /<noscript>[\s\S]*JavaScript/i.test(html)
      || /<script[\s\S]*>/i.test(html)
      || /<div[^>]+id=["'](root|app)["']/i.test(html);
}

// load Aidddo templates from /public via fetch (with small cache)
const AIDDDO_PATHS = {
  company: '/email-templates/aidddo/order.html',
  conf_de: '/email-templates/aidddo/order-confirmation-de.html',
  conf_en: '/email-templates/aidddo/order-confirmation-en.html',
} as const;

const templateCache: Record<string, string> = {};

async function loadAidddoTemplate(kind: 'company' | 'customer'): Promise<string> {
  const langIsEn = String(i18n.global.locale || '').toLowerCase().startsWith('en');
  const path = kind === 'company'
    ? AIDDDO_PATHS.company
    : (langIsEn ? AIDDDO_PATHS.conf_en : AIDDDO_PATHS.conf_de);

  if (templateCache[path]) return templateCache[path];

  const res = await fetch(path, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Failed to load email template: ${path}`);
  const html = await res.text();
  templateCache[path] = html;
  return html;
}

async function resolveHtmlTemplateForDomain(
  providedHtml: string,
  domain: Domain,
  kind: 'company' | 'customer'
): Promise<string> {
  const useAidddo =
    suffixToken(domain.urlSuffix) === 'spoerer' || 
    !providedHtml ||
    isLikelySpaIndex(providedHtml);

  if (useAidddo) {
    return await loadAidddoTemplate(kind);
  }
  return providedHtml;
}

function buildFallbackEmailHtml(
  data: Record<string, any>,
  domain: Domain,
  subject: string
): string {
  const { bgColor, textColor, logoUrl } = resolveVisuals(domain);

  const scanLinks = Array.isArray((data as any).scanUrls) && (data as any).scanUrls.length
    ? `<div style="margin-top:12px;">
         <div style="font-weight:600;margin-bottom:6px;">Scans</div>
         <ul style="padding-left:18px;margin:0;">
           ${(data as any).scanUrls.map((u: string) => `<li><a href="${u}">${u}</a></li>`).join('')}
         </ul>
       </div>`
    : '';

  const imageLinks = Array.isArray((data as any).imageUrls) && (data as any).imageUrls.length
    ? `<div style="margin-top:12px;">
         <div style="font-weight:600;margin-bottom:6px;">Images</div>
         <ul style="padding-left:18px;margin:0;">
           ${(data as any).imageUrls.map((u: string) => `<li><a href="${u}">${u}</a></li>`).join('')}
         </ul>
       </div>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>${subject}</title>
</head>
<body style="font-family: Arial, Helvetica, sans-serif; margin:0; padding:0; background:#f6f6f6;">
  <div style="max-width:700px;margin:20px auto;background:#fff;padding:20px;border-radius:8px;">
    <div style="text-align:center;margin-bottom:24px;">
      <img src="${logoUrl}" alt="Logo" style="max-width:160px;height:auto;"/>
      <h1 style="margin:16px 0 0;font-size:24px;">${subject}</h1>
    </div>

    <div style="background:${bgColor};color:${textColor};padding:16px;border-radius:6px;border-top:4px solid #888;border-bottom:4px solid #888;">
      <div style="font-size:18px;font-weight:700;margin-bottom:8px;">${data.coverModel ?? ''}</div>
      <div style="display:flex;gap:32px;justify-content:center;flex-wrap:wrap;">
        <div style="text-align:left;">
          <div>Body Side: ${data.bodySide ?? '-'}</div>
          <div>Calf Circumference: ${data.calfCircumferenceWithoutDeduction ?? '-'} mm</div>
          <div>Depth: ${data.depth ?? '-'} mm</div>
          <div>Color: ${data.color ?? '-'}</div>
        </div>
        <div style="text-align:left;">
          <div>Knee Joint: ${data.kneeJoint ?? '-'}</div>
          <div>Height: ${data.heightWithoutDeduction ?? '-'} mm</div>
          <div>Embossing: ${data.embossing ?? '-'}</div>
          <div>Zipper Color: ${data.zipColor ?? '-'}</div>
        </div>
      </div>
      <div style="height:1px;background:#d7d7d7;margin:24px 0;"></div>
      <div style="display:flex;gap:40px;justify-content:center;">
        <div style="text-align:center;">
          <div style="opacity:.9;">Quantity</div>
          <div style="font-size:28px;font-weight:700;">${data.quantity ?? '1'}</div>
        </div>
        <div style="text-align:center;">
          <div style="opacity:.9;">Price</div>
          <div style="font-size:28px;font-weight:700;">${data.price ?? '-'}</div>
        </div>
      </div>
    </div>

    ${scanLinks}
    ${imageLinks}

    <div style="text-align:center;margin-top:20px;color:#666;">
      <div>Total: <strong>${data.price ?? '-'}</strong></div>
      <div style="font-size:12px;margin-top:6px;">Plus VAT and shipping</div>
    </div>
  </div>
</body>
</html>`;
}

const getSelectedColors = (coverModel: string, colorList: (string | null)[]): [string, string] => {
  if(coverModel === "C-Flex Cover") {
    return[`${ i18n.global.t('components.part-selection.covers.bottom') }: ${colorList[0]}, ${ i18n.global.t('components.part-selection.covers.top') }: ${colorList[1]}`, colorList[2] || "-"]
  } else {
    return[`${ i18n.global.t('components.part-selection.covers.coverColor') }: ${colorList[0]}`, colorList[1] || "-"];
  }
}

const getAidddoOrderRequestData = (requestData: OrderService.OrderRequestData, userId: string, generatedOrderId: string) => {
  const colors = getSelectedColors(requestData.coverModel, requestData.colorList);
  return {
    company: requestData.company || '-',
    technicianFirstname: requestData.technicianFirstname || '-',
    technicianLastname: requestData.technicianLastname || '-',
    orderNr: generatedOrderId,
    mail: requestData.mail || '-',
    coverModel: removeWordFromString(requestData.coverModel || '-', 'phase1'),
    commissionNo: requestData.commissionNo || '-',
    customerNr: requestData.customerNr || userId || generateIDNumber('C'),
    bgColor: '#ededed',
    billingNr: '-',
    embossing: requestData.embossingList.join(', '),
    bodySide: requestData.bodySide || '-',
    kneeJoint: requestData.kneeJoint || '-',
    depth: requestData.depth.toString() || '-',
    heightWithoutDeduction: requestData.heightWithoutDeduction.toString() || '-',
    calfCircumferenceWithoutDeduction: requestData.calfCircumferenceWithoutDeduction.toString() || '-',
    color: colors[0],
    zipColor: colors[1],
    quantity: `1`,
    price: `€ ${requestData.price}`,
    logoUrl: cekoonLogoUrl,
    remarks: requestData.remarks,
  };
};

const getAidddoOrderConfirmationRequestData = (requestData: OrderService.OrderRequestData, userId: string, generatedOrderId: string) => {
  const colors = getSelectedColors(requestData.coverModel, requestData.colorList);
  return {
    company: requestData.company || '-',
    technicianFirstname: requestData.technicianFirstname || '-',
    technicianLastname: requestData.technicianLastname || '-',
    orderNr: generatedOrderId,
    mail: requestData.mail || '-',
    commissionNo: requestData.commissionNo || '-',
    customerNr: requestData.customerNr || userId || generateIDNumber('C'),
    billingNr: requestData.billingNr || '-',
    embossing: requestData.embossingList.join(', '),
    bodySide: requestData.bodySide || '-',
    kneeJoint: requestData.kneeJoint || '-',
    depth: requestData.depth.toString(),
    coverModel: removeWordFromString(requestData.coverModel || '-', 'phase1'),
    heightWithoutDeduction: requestData.heightWithoutDeduction.toString() || '-',
    bgColor: '#ededed',
    calfCircumferenceWithoutDeduction: requestData.calfCircumferenceWithoutDeduction.toString() || '-',
    color: colors[0],
    zipColor: colors[1],
    quantity: `1`,
    price: `€ ${requestData.price}`,
    logoUrl: cekoonLogoUrl,
    remarks: requestData.remarks,
  };
};

// wjtortho
const getWjtorthoOrderRequestData = (requestData: OrderService.OrderRequestData, generatedOrderId: string) => {
  const colors = getSelectedColors(requestData.coverModel, requestData.colorList);
  return {
    company: requestData.company || '-',
    technicianFirstname: requestData.technicianFirstname || '-',
    technicianLastname: requestData.technicianLastname || '-',
    orderNr: generatedOrderId,
    customerNr: requestData.customerNr || generateIDNumber('C'),
    mail: requestData.mail,
    bgColor: '#007AC2',
    coverModel: removeWordFromString(requestData.coverModel || '-', 'phase1'),
    commissionNo: requestData.commissionNo || '-',
    billingNr: requestData.billingNr || '-',
    embossing: requestData.embossingList.join(', '),
    bodySide: requestData.bodySide || '-',
    kneeJoint: requestData.kneeJoint || '-',
    depth: requestData.depth.toString() || '-',
    heightWithoutDeduction: requestData.heightWithoutDeduction.toString(),
    calfCircumferenceWithoutDeduction: requestData.calfCircumferenceWithoutDeduction.toString(),
    color: colors[0],
    zipColor: colors[1],
    quantity: `1`,
    price: `€ ${requestData.price}`,
    logoUrl: wjtorthoLogoUrl,
    remarks: requestData.remarks,
  };
};

const getWjtorthoOrderConfirmationRequestData = (requestData: OrderService.OrderRequestData, userId: string, generatedOrderId: string) => {
  const colors = getSelectedColors(requestData.coverModel, requestData.colorList);
  return {
    company: requestData.company || '-',
    technicianFirstname: requestData.technicianFirstname || '-',
    technicianLastname: requestData.technicianLastname || '-',
    orderNr: generatedOrderId,
    mail: requestData.mail || '-',
    commissionNo: requestData.commissionNo || '-',
    customerNr: requestData.customerNr || userId || generateIDNumber('C'),
    billingNr: requestData.billingNr || '-',
    embossing: requestData.embossingList.join(', '),
    bodySide: requestData.bodySide || '-',
    kneeJoint: requestData.kneeJoint || '-',
    depth: requestData.depth.toString() || '-',
    coverModel: removeWordFromString(requestData.coverModel || '-', 'phase1'),
    bgColor: '#007AC2',
    textColor: '#FFF',
    heightWithoutDeduction: requestData.heightWithoutDeduction.toString() || '-',
    calfCircumferenceWithoutDeduction: requestData.calfCircumferenceWithoutDeduction.toString() || '-',
    color: colors[0],
    zipColor: colors[1],
    quantity: `1`,
    price: `€ ${requestData.price}`,
    logoUrl: wjtorthoLogoUrl,
    remarks: requestData.remarks,
  };
};

// luttermann
const getLuttermanOrderRequestData = (requestData: OrderService.OrderRequestData, generatedOrderId: string) => {
  const colors = getSelectedColors(requestData.coverModel, requestData.colorList);
  return {
    company: requestData.company || '-',
    technicianFirstname: requestData.technicianFirstname || '-',
    technicianLastname: requestData.technicianLastname || '-',
    orderNr: generatedOrderId,
    mail: requestData.mail || '-',
    commissionNo: requestData.commissionNo || '-',
    customerNr: requestData.customerNr || generateIDNumber('C'),
    coverModel: removeWordFromString(requestData.coverModel || '-', 'phase1'),
    bgColor: '#204a9e',
    billingNr: requestData.billingNr || '-',
    embossing: requestData.embossingList.join(', '),
    bodySide: requestData.bodySide || '-',
    kneeJoint: requestData.kneeJoint || '-',
    depth: requestData.depth.toString(),
    heightWithoutDeduction: requestData.heightWithoutDeduction.toString() || '-',
    calfCircumferenceWithoutDeduction: requestData.calfCircumferenceWithoutDeduction.toString() || '-',
    color: colors[0],
    zipColor: colors[1],
    quantity: `1`,
    price: `€ ${requestData.price}`,
    logoUrl: luttermannLogoUrl,
    technician: requestData.technician || '-',
    vgNumber: requestData.vgNumber || '-',
    vsgNumber: requestData.vsgNumber || '-',
    remarks: requestData.remarks,
  };
};

const getLuttermanOrderConfirmationRequestData = (requestData: OrderService.OrderRequestData, userId: string, generatedOrderId: string) => {
  const colors = getSelectedColors(requestData.coverModel, requestData.colorList);
  return {
    company: requestData.company || '-',
    technicianFirstname: requestData.technicianFirstname || '-',
    technicianLastname: requestData.technicianLastname || '-',
    orderNr: generatedOrderId,
    mail: requestData.mail || '-',
    commissionNo: requestData.commissionNo || '-',
    customerNr: requestData.customerNr || userId || generateIDNumber('C'),
    billingNr: requestData.billingNr || '-',
    embossing: requestData.embossingList.join(', '),
    bodySide: requestData.bodySide || '-',
    kneeJoint: requestData.kneeJoint || '-',
    depth: requestData.depth.toString() || '-',
    coverModel: removeWordFromString(requestData.coverModel || '-', 'phase1'),
    bgColor: '#204a9e',
    textColor: '#FFF',
    heightWithoutDeduction: requestData.heightWithoutDeduction.toString() || '-',
    calfCircumferenceWithoutDeduction: requestData.calfCircumferenceWithoutDeduction.toString() || '-',
    color: colors[0],
    zipColor: colors[1],
    quantity: `1`,
    price: `€ ${requestData.price}`,
    logoUrl: luttermannLogoUrl,
    remarks: requestData.remarks,
    technician: requestData.technician || '-',
    vgNumber: requestData.vgNumber || '-',
    vsgNumber: requestData.vsgNumber || '-',
  };
};

// For company
export const sendEmailOrderTemplate = async ({
  HTMLorderTemplate,
  requestData,
  userId,
  domain,
  generatedOrderId,
  scanUrls,
  imageUrls,
}: {
  HTMLorderTemplate: string;
  requestData: OrderService.OrderRequestData;
  userId: string;
  domain: Domain;
  generatedOrderId: string;
  scanUrls: string[];
  imageUrls: string[];
}) => {
  //guard
  assertNotSpaIndex(HTMLorderTemplate, domain);

  const userStore = useUserStore();
  const key = suffixToken(domain.urlSuffix);
  const isWjtortho = key === 'wjtortho';
  const isLuttermann = key === 'luttermann' || key === 'lutterman';
  const storeEmail = userStore.user?.email;
  if (!storeEmail) {
    throw new Error('User email is undefined');
  }
  const user = await UserService.getUserByEmail(storeEmail);
  const additionalMails = Array.isArray(user.additionalEmails) ? user.additionalEmails.join(',') : '';
  let data: Record<string, string | string[]> = getAidddoOrderRequestData(requestData, userId, generatedOrderId);
  let email = additionalMails;
  if(!email){
    email = "office@wako3d.com";
  }
  if (isWjtortho){
    data = getWjtorthoOrderRequestData(requestData, generatedOrderId);

  } 
  else if (isLuttermann) {
    data = getLuttermanOrderRequestData(requestData, generatedOrderId);

  } 

  //apply visuals for ANY domain
  const visuals = resolveVisuals(domain);
  data = {
    ...data,
    bgColor: visuals.bgColor,
    ...(visuals.textColor ? { textColor: visuals.textColor } : {}),
    logoUrl: (data as any).logoUrl || visuals.logoUrl,
    scanUrls,
    imageUrls,
  };

  const templateSource = await resolveHtmlTemplateForDomain(HTMLorderTemplate, domain, 'company');
  const updatedTemplateRaw = replacePlaceholders(templateSource, data);
  const updatedTemplate = isLikelySpaIndex(updatedTemplateRaw)
    ? buildFallbackEmailHtml(data as Record<string, any>, domain, 'Order')
    : updatedTemplateRaw;
  
  const formData = new FormData();
  formData.append('to', email);
  formData.append('subject', 'Order');
  formData.append('html', updatedTemplate);
  formData.append('body', '');

  try {
    const response = await apiExecutePost(`${emailServiceUrl}/send-email`, formData);
    console.log('Response from server:', response);
  } catch (error) {
    console.error('Error during API call:', error);
  }
};

// For customer
export const sendEmailOrderConfirmationTemplate = async ({
  HTMLorderConfirmationTemplate,
  requestData,
  domain,
  userId,
  generatedOrderId,
}: {
  HTMLorderConfirmationTemplate: string;
  requestData: OrderService.OrderRequestData;
  domain: Domain;
  userId: string;
  generatedOrderId: string;
}) => {
  //guard
  assertNotSpaIndex(HTMLorderConfirmationTemplate, domain);

  const key = suffixToken(domain.urlSuffix);
  const isWjtortho = key === 'wjtortho';
  const isLuttermann = key === 'luttermann' || key === 'lutterman';
  let data: Record<string, string> =
  getAidddoOrderConfirmationRequestData(
    requestData,
    userId,
    generatedOrderId
  );
  if (isWjtortho) data = getWjtorthoOrderConfirmationRequestData(requestData, userId, generatedOrderId);
  if (isLuttermann) data = getLuttermanOrderConfirmationRequestData(requestData, userId, generatedOrderId);

  //apply visuals
  const visuals = resolveVisuals(domain);
  data = {
    ...data,
    bgColor: visuals.bgColor,
    ...(visuals.textColor ? { textColor: visuals.textColor } : {}),
    logoUrl: (data as any).logoUrl || visuals.logoUrl,
  };

  //Force Spoerer (or SPA-like) to Aidddo layout before replacement
  const templateSource = await resolveHtmlTemplateForDomain(HTMLorderConfirmationTemplate, domain, 'customer');

  const updatedTemplateRaw = replacePlaceholders(templateSource, data);
  const updatedTemplate = isLikelySpaIndex(updatedTemplateRaw)
    ? buildFallbackEmailHtml(data as Record<string, any>, domain, 'Order Confirmation')
    : updatedTemplateRaw;

  const pdfTemplate = isWjtortho
    ? getOrderConfirmationPDFTemplateWjtOrtho(data)
    : getOrderConfirmationPDFTemplate(data);

  const updatedPdfTemplates = replacePlaceholders(pdfTemplate, data);

  const formData = new FormData();
  formData.append('to', requestData.mail);
  formData.append('subject', 'Order Confirmation');
  formData.append('html', updatedTemplate);
  formData.append('body', '');

  const pdfContent = getHTMLTemplateElement(updatedPdfTemplates);
  const pdfBlob = await getGeneratedPDFBlob(pdfContent);
  if (pdfBlob) {
    const pdfFile = new File([pdfBlob], 'order-confirmation.pdf', { type: 'application/pdf' });
    formData.append('file', pdfFile);
  }

  try {
    const response = await apiExecutePost(`${emailServiceUrl}/send-email`, formData);
    console.log('Response from server:', response);
  } catch (error) {
    console.error('Error during API call:', error);
  }
};

const getHTMLTemplateElement = (htmlTemplate: string) => {
  const container = document.createElement('div');
  container.innerHTML = htmlTemplate;
  return container;
};
